import ApiEndPoints from "@/constants/apiEndpoints";
import api from "@/lib/api";
import { dedup } from "@/lib/api/dedup";
import { csrfHeaders, handledErrorConfig } from "@/lib/api/headers";
import type { IApiEnvelope } from "@/types/common";
import type {
  IAccountExport,
  IChangePasswordRequest,
  IDeleteAccountRequest,
  IEmailSimulatorMessage,
  IEmailVerificationIssue,
  ITwoFactorConfirmResult,
  ITwoFactorSetupResult,
  IUpdateProfileRequest,
  IUserProfile,
  IVerifyEmailRequest,
} from "@/types/profile";

export async function getProfile(): Promise<IUserProfile> {
  return dedup(`GET:${ApiEndPoints.ME.PROFILE}`, async () => {
    const payload = await api.get<IApiEnvelope<IUserProfile>>(ApiEndPoints.ME.PROFILE);
    return payload.data;
  });
}

export async function updateProfile(data: IUpdateProfileRequest): Promise<IUserProfile> {
  const payload = await api.put<IApiEnvelope<IUserProfile>>(ApiEndPoints.ME.PROFILE, data, {
    headers: csrfHeaders(),
    ...handledErrorConfig,
  });
  return payload.data;
}

export async function uploadAvatar(file: File): Promise<IUserProfile> {
  const form = new FormData();
  form.append("avatar", file);
  const payload = await api.post<IApiEnvelope<IUserProfile>>(ApiEndPoints.ME.AVATAR, form, {
    headers: { ...csrfHeaders(), "Content-Type": "multipart/form-data" },
    ...handledErrorConfig,
  });
  return payload.data;
}

export async function deleteAvatar(): Promise<IUserProfile> {
  const payload = await api.delete<IApiEnvelope<IUserProfile>>(ApiEndPoints.ME.AVATAR, {
    headers: csrfHeaders(),
  });
  return payload.data;
}

export async function changePassword(data: IChangePasswordRequest): Promise<void> {
  await api.post<IApiEnvelope<null>>(ApiEndPoints.ME.CHANGE_PASSWORD, data, {
    headers: csrfHeaders(),
    ...handledErrorConfig,
  });
}

export async function setupTwoFactor(): Promise<ITwoFactorSetupResult> {
  const payload = await api.post<IApiEnvelope<ITwoFactorSetupResult>>(
    ApiEndPoints.ME.TWO_FACTOR_SETUP,
    {},
    { headers: csrfHeaders(), ...handledErrorConfig },
  );
  return payload.data;
}

export async function confirmTwoFactor(code: string): Promise<ITwoFactorConfirmResult> {
  const payload = await api.post<IApiEnvelope<ITwoFactorConfirmResult>>(
    ApiEndPoints.ME.TWO_FACTOR_CONFIRM,
    { code },
    { headers: csrfHeaders(), ...handledErrorConfig },
  );
  return payload.data;
}

export async function disableTwoFactor(code: string): Promise<void> {
  await api.post<IApiEnvelope<null>>(
    ApiEndPoints.ME.TWO_FACTOR_DISABLE,
    { code },
    { headers: csrfHeaders(), ...handledErrorConfig },
  );
}

export async function exportAccountData(): Promise<IAccountExport> {
  const payload = await api.get<IApiEnvelope<IAccountExport>>(ApiEndPoints.ME.ACCOUNT_EXPORT);
  return payload.data;
}

export async function deleteAccount(data: IDeleteAccountRequest): Promise<void> {
  await api.delete<IApiEnvelope<null>>(ApiEndPoints.ME.ACCOUNT, {
    data,
    headers: csrfHeaders(),
    ...handledErrorConfig,
  });
}

export async function requestEmailVerification(email: string): Promise<IEmailVerificationIssue> {
  const payload = await api.post<IApiEnvelope<IEmailVerificationIssue>>(
    ApiEndPoints.ME.EMAIL_REQUEST,
    { email },
    { headers: csrfHeaders(), ...handledErrorConfig },
  );
  return payload.data;
}

export async function verifyEmail(data: IVerifyEmailRequest): Promise<IUserProfile> {
  const payload = await api.post<IApiEnvelope<IUserProfile>>(
    ApiEndPoints.ME.EMAIL_VERIFY,
    data,
    { headers: csrfHeaders(), ...handledErrorConfig },
  );
  return payload.data;
}

export async function getEmailSimulatorMessages(): Promise<IEmailSimulatorMessage[]> {
  const payload = await api.get<IApiEnvelope<IEmailSimulatorMessage[]>>(
    ApiEndPoints.ME.EMAIL_SIMULATOR,
    { ...handledErrorConfig },
  );
  return payload.data;
}
